import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { ICreatePasswordEntry, IDecryptedPasswordEntry } from '../types/vault.types';
import { VaultService } from '../services/vault.service';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { useWorkspace } from './WorkspaceContext';
import { useAppSelector } from '../hooks/useRedux';

interface VaultContextType {
    entries: IDecryptedPasswordEntry[];
    favoriteEntries: IDecryptedPasswordEntry[];
    loading: boolean;
    error: string | null;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    refreshEntries: () => Promise<void>;
    // refreshFavoriteEntries: () => Promise<void>;
    addEntry: (entry: ICreatePasswordEntry) => Promise<IDecryptedPasswordEntry>;
    updateEntry: (id: string, entry: Partial<ICreatePasswordEntry>) => Promise<void>;
    deleteEntry: (entryId: string, isShared?: boolean, sharedKey?: string) => Promise<boolean>;
    searchEntries: (query: string) => Promise<void>;
    generateEncryptionKey: () => string;
    encryptWithVaultKey: (data: string) => string;
}

const VaultContext = createContext<VaultContextType | undefined>(undefined);

export const VaultProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [entries, setEntries] = useState<IDecryptedPasswordEntry[]>([]);
    const [favoriteEntries, setFavoriteEntries] = useState<IDecryptedPasswordEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const { isAuthenticated } = useAuth();
    const { activeWorkspace } = useWorkspace();
    const { team_id } = useAppSelector(state => state.auth);
    
    // Track if we're currently fetching data
    const isFetching = useRef(false);
    
    const refreshEntries = useCallback(async () => {
        // Prevent duplicate fetches, but don't check loading state
        if (isFetching.current) {
            return;
        }
        
        try {
            isFetching.current = true;
            setLoading(true);
            setError(null);

            // Pass the current workspace ID when fetching entries
            const workspaceId = activeWorkspace?.id || team_id || undefined;
            const fetchedEntries = await VaultService.getAllEntries(workspaceId);
            setEntries(fetchedEntries);
        } catch (err) {
            console.error('Error fetching entries:', err);
            setError('Failed to fetch entries');
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, [activeWorkspace?.id, team_id]);

    // const refreshFavoriteEntries = async () => {
    //     try {
    //         setLoading(true);
    //         setError(null);
    //         const favoriteEntries = await VaultService.getFavoriteEntries();
    //         setFavoriteEntries(favoriteEntries);
    //     } catch (err) {
    //         setError('Failed to fetch favourite entries');
    //         console.error(err);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const addEntry = async (entry: ICreatePasswordEntry) => {
        try {
            setLoading(true);
            setError(null);
            
            // Pass the current workspace ID when creating a new entry
            const workspaceId = activeWorkspace?.id || team_id || undefined;
            const newEntry = await VaultService.createEntry(entry, workspaceId);
            
            // Update the entries list with the new entry
            setEntries(prevEntries => {
                // Remove any existing entry with the same ID (if it exists)
                const filteredEntries = prevEntries.filter(e => e.id !== newEntry.id);
                const updatedEntries = [...filteredEntries, newEntry];
                return updatedEntries;
            });
            refreshEntries();
            // Show success message
            // toast.success('Password added successfully');
            return newEntry;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to add entry';
            setError(errorMessage);
            toast.error(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const updateEntry = async (id: string, entry: Partial<ICreatePasswordEntry>) => {
        try {
            setLoading(true);
            setError(null);

            // Ensure the favourite field is a boolean
            const updatedEntry = {
                ...entry,
                favourite: entry.favourite === true // Ensure it's a boolean
            };

            // Pass the current workspace ID when updating an entry
            const workspaceId = activeWorkspace?.id || team_id || undefined;
            await VaultService.updateEntry(id, updatedEntry, workspaceId);
            
            // Update the entry in the local state
            const updatedEntryData = await VaultService.getEntryById(id);
            setEntries(prevEntries => {
                return prevEntries.map(e => e.id === id ? updatedEntryData : e);
            });
            
            // Show success message
            toast.success('Password updated successfully');
        } catch (err) {
            setError('Failed to update entry');
            toast.error('Failed to update entry');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deleteEntry = async (entryId: string, isShared = false, sharedKey?: string) => {
        try {
            setLoading(true);
            await VaultService.deleteEntry(entryId, isShared, sharedKey);
            
            // Update local state by removing the deleted entry
            setEntries(prevEntries => prevEntries.filter(entry => entry.id !== entryId));
            
            return true;
        } catch (error) {
            console.error('Error deleting entry:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const searchEntries = async (query: string) => {
        try {
            setLoading(true);
            setError(null);
            const searchResults = await VaultService.searchEntries(query);
            setEntries(searchResults);
            setLoading(false);
        } catch (err) {
            setError('Failed to search entries');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const generateEncryptionKey = () => {
        // Generate a random encryption key
        const array = new Uint8Array(32);
        window.crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    };
    
    const encryptWithVaultKey = (data: string) => {
        // This is a placeholder - in a real implementation, this would encrypt the data
        // using the vault key
        return data; // For now, just return the data as-is
    };

    // Update the useEffect to handle workspace changes
    useEffect(() => {
        if (isAuthenticated) {
            refreshEntries();
        } else {
            // Clear entries when not authenticated
            setEntries([]);
        }
    }, [isAuthenticated, activeWorkspace?.id, team_id, refreshEntries]);

    return (
        <VaultContext.Provider
            value={{
                entries,
                favoriteEntries,
                loading,
                error,
                searchQuery,
                setSearchQuery,
                refreshEntries,
                // refreshFavoriteEntries,
                addEntry, 
                updateEntry,
                deleteEntry,
                searchEntries,
                generateEncryptionKey,
                encryptWithVaultKey
            }}
        >
            {children}
        </VaultContext.Provider>
    );
};

export const useVault = () => {
    const context = useContext(VaultContext);
    if (context === undefined) {
        throw new Error('useVault must be used within a VaultProvider');
    }
    return context;
};